<template>
  <el-container ref="html" class="html-material" v-loading="loading">
    <perfect-scrollbar
      :ref="scrollbar"
      class="html-wrapper zoom-active"
      :style="{ height: htmlHeight }"
      v-dragscroll="true"
      @mouseenter.once="updateHtmlContent"
    >
      <el-card class="box-card content">
        <el-row>
          <el-col>
            <div class="container">
              <img :src="articleImage" class="html-image" />
              <el-card class="title-box translation-box">
                <el-row>
                  <p id="level" :class="['level', articleLevelBg]">
                    {{ $t('discoveryMaterial.level') + ' ' + articleLevels }}
                  </p>
                  <el-col :span="16">
                    <h1 v-if="articleTitle" class="title">
                      {{ articleTitle }}
                    </h1>
                  </el-col>
                  <el-col :span="8">
                    <p class="txt">
                      <span class="article-slash">／</span>
                      <span class="article-no">NO. </span>
                      <span class="article-id">{{ articleId }}</span>
                    </p>
                  </el-col>
                </el-row>
              </el-card>
            </div>
          </el-col>
          <el-col>
            <el-divider v-if="articleAuthor" content-position="left">
              by {{ articleAuthor }}
            </el-divider>
          </el-col>
          <el-col>
            <div
              id="html-content"
              class="html-content"
              v-dompurify-html="htmlContent"
              :style="contentFontSize"
            ></div>
          </el-col>
        </el-row>
      </el-card>
    </perfect-scrollbar>
    <div class="material-zoom">
      <div class="material-fullscreen">
        <el-button @click="fullScreenMaterial" circle>
          <img :src="zoomIcon" />
        </el-button>
      </div>
    </div>
  </el-container>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';

import { dragscroll } from 'vue-dragscroll';
import VocabularyTooltip from '@/mixins/vocabularyTooltip';

export default {
  mixins: [ VocabularyTooltip ],
  directives: { dragscroll },
  computed: {
    ...mapState('material', [
      'htmlContent',
      'articleTitle',
      'articleAuthor',
      'articleImage',
      'articleId',
      'articleLevel',
      'htmlZoom',
      'htmlHeight',
      'fontSize',
      'levelBgWidth',
      'levelFontSize',
      'htmlTitleSize',
      'lineHeight'
    ]),
    ...mapState('layout', ['isMaterialFullScreen', 'materialViewportHeight', 'isIpadDevice']),
    ...mapGetters('layout', ['isDefaultLayout']),
    ...mapGetters(['isStudent']),
    ...mapGetters('material', ['articleLevels']),

    // Uses materialViewportHeight (frozen while an on-screen keyboard is
    // open) rather than the live viewportHeight — see PDFMaterial.vue's
    // materialHeight for why. .kiro/specs/lesson-room-viewport-keyboard.
    htmlHeight() {
      const initHeight = this.materialViewportHeight - this.otherElementsHeight;
      if (this.isMobileWidth) {
        return this.isMaterialFullScreen
          ? initHeight + 'px'
          : initHeight - this.videoSize + 'px';
      }
      return initHeight + 'px';
    },
    otherElementsHeight() {
      const mobileFooter = 60;
      const headerAndMaterialInfo = 132;
      const base = this.isMobileWidth ? mobileFooter : headerAndMaterialInfo;
      return base + this.ipadHeightOffset;
    },

    /**
     * Extra height (px) iPad Safari needs subtracted beyond other devices.
     * See PDFMaterial.vue's `ipadHeightOffset` for the rationale (mirrors
     * the existing `.iPad .content_3 .chatbox` CSS precedent in main.css).
     * See .kiro/specs/lesson-room-viewport-keyboard/design.md.
     */
    ipadHeightOffset() {
      return this.isIpadDevice ? 107 : 0;
    },
    isMobileWidth() {
      return this.windowWidth < 960;
    },
    articleLevelBg() {
      return `level${this.articleLevel}`;
    },
    zoomIcon() {
      const icon = this.isMaterialFullScreen
        ? 'text_zoomout_sp@2x.png'
        : 'text_zoom_sp@2x.png';
      return require(`@/assets/img/${icon}`);
    },
    contentFontSize() {
      let content = document.querySelectorAll(
        'div.content p, div.content h2, div.content li, div.content h1, .el-divider__text'
      );

      if (!this.isMobileWidth && content) {
        for (let i = 0; i < content.length; i++) {
          if (content[i].classList[0] === 'level') {
            content[i].style.width = this.levelBgWidth + 'px';
            content[i].style.fontSize = this.levelFontSize + 'px';
          } else if (content[i].classList[0] === 'title') {
            content[i].style.fontSize = this.htmlTitleSize + 'px';
          } else {
            content[i].style.fontSize = this.fontSize + 'px';
            content[i].style.lineHeight = this.lineHeight + 'px';
          }
        }
      }

      return { 'font-size': this.fontSize + 'px' };
    }
  },
  data() {
    return {
      scrollbar: 'html-scrollbar',
      windowWidth: window.innerWidth,
      videoSize: 0,
      loading: true
    };
  },
  mounted() {
    this.videoSize = document.querySelector('#video-column').offsetHeight;
    window.addEventListener('resize', () => {
      // Height comes from the shared layout.viewportHeight (visible viewport);
      // only width and the video column size are tracked locally here.
      this.windowWidth = window.innerWidth;
      this.videoSize = document.querySelector('#video-column').offsetHeight;
    });
  },
  watch: {
    htmlContent(val) {
      if (val) {
        this.loading = false;
      }
    }
  },
  methods: {
    ...mapMutations('layout', ['materialFullScreen']),

    showPerfectScrollbar() {
      let vm = this;
      vm.$nextTick(() => {
        vm.$refs[this.scrollbar].update();
      });
    },
    fullScreenMaterial() {
      this.materialFullScreen();
    },
    updateHtmlContent() {
      // pass the html element as html collection
      if (this.isStudent) {
        const htmlCollection = document.querySelectorAll('.box-card.content')[0].children;
        this.initializedTooltip(htmlCollection);
      }
    }
  }
};
</script>

<style scoped src="@/assets/css/pc/discovery_material.css"></style>
<style scoped src="@/assets/css/pc/vocabulary_tooltip.css"></style>
